/**
 * InstallPrompt (Sprint 9 B5) — PWA install banner.
 *
 * Behavior:
 *   - Track page view count qua localStorage `sep2026-pv-count`
 *   - Listen `beforeinstallprompt` event, preventDefault để giữ deferredPrompt
 *     (browser native PWA install prompt API)
 *   - Show banner khi: PV ≥ 3 VÀ deferredPrompt available VÀ NOT dismissed
 *   - "Cài đặt ứng dụng" → call deferredPrompt.prompt() → native browser dialog
 *   - "Để sau" → set localStorage flag, không show lại
 *
 * Mount ở root layout (vd AppLayout) để counter increment khi user navigate
 * giữa các page. Background không occupy space khi không show — render null.
 */

import { useEffect, useState } from "react";
import { Alert, Button, Space } from "antd";
import { DownloadOutlined } from "@ant-design/icons";

const PV_KEY = "sep2026-pv-count"; // gitleaks:allow — localStorage key, không phải secret
const DISMISSED_KEY = "sep2026-install-prompt-dismissed"; // gitleaks:allow
const MIN_PV_BEFORE_PROMPT = 3;

/**
 * Browser PWA install prompt event — không có TS DOM lib chuẩn.
 * Chrome/Edge/Safari iOS đều fire `beforeinstallprompt` với shape này.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [pvCount, setPvCount] = useState<number>(() => {
    const stored = Number(window.localStorage.getItem(PV_KEY) ?? "0");
    return Number.isFinite(stored) ? stored : 0;
  });
  const [dismissed, setDismissed] = useState<boolean>(
    () => window.localStorage.getItem(DISMISSED_KEY) === "1",
  );

  // Tăng PV counter mỗi lần component mount (mỗi navigate sang page mới
  // sẽ remount AppLayout nếu route key đổi — nhưng AppLayout thường shared,
  // nên dùng useEffect trên window.location.pathname để track navigation
  // chuẩn hơn. Sprint 9 B5 dùng pattern mount-increment đơn giản, đủ cho
  // first-time invite/signup → 3 navigates).
  useEffect(() => {
    const newCount = pvCount + 1;
    window.localStorage.setItem(PV_KEY, String(newCount));
    setPvCount(newCount);
    // Disable exhaustive-deps: chỉ chạy 1 lần mount, không re-run khi pvCount đổi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = (event: Event) => {
      const e = event as BeforeInstallPromptEvent;
      // Ngăn browser show prompt mặc định — em control timing
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") {
        // User accept → clear prompt, không show lại
        setDeferredPrompt(null);
      }
    } catch {
      // ignore — user có thể cancel native dialog
    }
  };

  const handleDismiss = () => {
    window.localStorage.setItem(DISMISSED_KEY, "1");
    setDismissed(true);
  };

  // Conditions để show banner — tất cả phải true
  const shouldShow =
    !dismissed && deferredPrompt !== null && pvCount >= MIN_PV_BEFORE_PROMPT;

  if (!shouldShow) {
    return null;
  }

  return (
    <Alert
      type="info"
      showIcon={false}
      style={{ marginBottom: 16 }}
      message="Cài đặt Savina E-learning vào màn hình chính"
      description="Truy cập nhanh hơn, hoạt động offline với bài đã download, không cần mở trình duyệt."
      action={
        <Space>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleInstall}
          >
            Cài đặt ứng dụng
          </Button>
          <Button onClick={handleDismiss}>Để sau</Button>
        </Space>
      }
    />
  );
}

export default InstallPrompt;
